import React, { useState } from 'react';
import { FiAlertTriangle, FiX, FiSend, FiCheckCircle } from 'react-icons/fi';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

function ReportIssueButton() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    type: 'bug',
    message: '',
    page: ''
  });

  // Don't show if not logged in
  if (!user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;

    setLoading(true);
    try {
      await api.submitIssue({
        type: form.type,
        message: form.message,
        page: window.location.pathname,
        browser: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`
      });
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setForm({ type: 'bug', message: '', page: '' });
      }, 2000);
    } catch (err) {
      console.error('Failed to submit issue:', err);
      alert('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-red-500/90 hover:bg-red-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all group"
        title="Report an issue"
      >
        <FiAlertTriangle size={20} />
        <span className="hidden group-hover:inline text-sm font-medium">Report Issue</span>
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-secondary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <FiAlertTriangle className="text-red-400" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Report an Issue</h3>
                  <p className="text-xs text-muted-foreground">Help us improve FirstCut</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Content */}
            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <FiCheckCircle className="text-emerald-400" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Thank you!</h4>
                <p className="text-muted-foreground">We've received your report and will look into it.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Issue Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Issue Type</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'bug', label: '🐛 Bug', desc: 'Something broken' },
                      { id: 'feature', label: '💡 Feature', desc: 'Suggestion' },
                      { id: 'other', label: '❓ Other', desc: 'General' },
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setForm({ ...form, type: type.id })}
                        className={`flex-1 p-3 rounded-lg border text-left transition-all ${
                          form.type === type.id
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border bg-secondary/30 text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        <div className="text-sm font-medium">{type.label}</div>
                        <div className="text-xs opacity-70">{type.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    What happened?
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Describe the issue or your suggestion..."
                    rows={4}
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    required
                  />
                </div>

                {/* Context Info */}
                <div className="p-3 bg-secondary/50 rounded-lg text-xs text-muted-foreground">
                  <p>We'll also capture:</p>
                  <ul className="mt-1 space-y-0.5">
                    <li>• Current page: <span className="text-foreground">{window.location.pathname}</span></li>
                    <li>• Your email: <span className="text-foreground">{user?.email}</span></li>
                    <li>• Browser info (for debugging)</li>
                  </ul>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !form.message.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <span className="animate-pulse">Submitting...</span>
                  ) : (
                    <>
                      <FiSend size={18} />
                      Submit Report
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ReportIssueButton;
